package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowBack
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.FruxStepIndicator
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.wizard_back_to_place
import fr.frux.feature.treeadd.resources.wizard_back_to_species
import fr.frux.feature.treeadd.resources.wizard_cancel
import fr.frux.feature.treeadd.resources.wizard_segment_details
import fr.frux.feature.treeadd.resources.wizard_segment_place
import fr.frux.feature.treeadd.resources.wizard_segment_species
import fr.frux.feature.treeadd.resources.wizard_step_counter
import fr.frux.feature.treeadd.resources.wizard_step_counter_neutral
import fr.frux.feature.treeadd.resources.wizard_step_state
import fr.frux.feature.treeadd.resources.wizard_step_state_neutral
import fr.frux.feature.treeadd.resources.wizard_title_add
import fr.frux.feature.treeadd.resources.wizard_title_place
import org.jetbrains.compose.resources.stringResource

private val HEADER_SIDE_SLOT = 104.dp
private val HEADER_CORNER = 28.dp

/** Indicateur des trois segments, partagé par l'en-tête et la colonne grand écran de l'étape 1. */
@Composable
internal fun WizardStepIndicator(
    step: WizardStep,
    modifier: Modifier = Modifier,
) {
    val index = step.ordinal
    val labels =
        listOf(
            stringResource(Res.string.wizard_segment_place),
            stringResource(Res.string.wizard_segment_species),
            stringResource(Res.string.wizard_segment_details),
        )
    FruxStepIndicator(
        labels = labels,
        currentIndex = index,
        // Avant le choix « arbre ou lieu », le nombre d'étapes n'est pas connu : pas de « sur 3 » (#652).
        stateDescription =
            if (step == WizardStep.PLACE) {
                stringResource(Res.string.wizard_step_state_neutral, index + 1, labels[index])
            } else {
                stringResource(Res.string.wizard_step_state, index + 1, labels[index])
            },
        modifier = modifier,
    )
}

/**
 * En-tête de l'assistant. À l'étape Placer sur téléphone il flotte au-dessus de la carte (surface
 * aux coins bas arrondis). Aux étapes suivantes il est posé sur le fond, avec un filet bas, la
 * flèche de retour à gauche et « Annuler » à droite.
 */
@Composable
internal fun WizardHeader(
    step: WizardStep,
    onBack: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier,
) {
    if (step == WizardStep.PLACE) {
        PlaceHeader(onCancel, modifier)
    } else {
        StepHeader(step, onBack, onCancel, modifier)
    }
}

@Composable
private fun PlaceHeader(
    onCancel: () -> Unit,
    modifier: Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(bottomStart = HEADER_CORNER, bottomEnd = HEADER_CORNER),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 2.dp,
    ) {
        Column(
            modifier =
                Modifier
                    .windowInsetsPadding(
                        WindowInsets.statusBars,
                    ).padding(start = 8.dp, top = 8.dp, end = 8.dp, bottom = 14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(modifier = Modifier.width(HEADER_SIDE_SLOT), contentAlignment = Alignment.CenterStart) {
                    CancelButton(onCancel)
                }
                Column(modifier = Modifier.weight(1f), horizontalAlignment = Alignment.CenterHorizontally) {
                    StepCounter(WizardStep.PLACE)
                    Text(
                        text = stringResource(Res.string.wizard_title_place),
                        style = MaterialTheme.typography.titleLarge,
                    )
                }
                Spacer(Modifier.width(HEADER_SIDE_SLOT))
            }
            WizardStepIndicator(WizardStep.PLACE, Modifier.padding(horizontal = 12.dp))
        }
    }
}

@Composable
private fun StepHeader(
    step: WizardStep,
    onBack: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier,
) {
    val outline = MaterialTheme.colorScheme.outlineVariant
    Column(modifier = modifier.fillMaxWidth().background(MaterialTheme.colorScheme.background)) {
        Column(
            modifier =
                Modifier
                    .windowInsetsPadding(
                        WindowInsets.statusBars,
                    ).padding(start = 8.dp, top = 8.dp, end = 4.dp, bottom = 12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                FruxIconButton(
                    icon = Icons.AutoMirrored.Outlined.ArrowBack,
                    contentDescription =
                        if (step == WizardStep.SPECIES) {
                            stringResource(Res.string.wizard_back_to_place)
                        } else {
                            stringResource(Res.string.wizard_back_to_species)
                        },
                    onClick = onBack,
                )
                Column(modifier = Modifier.weight(1f)) {
                    StepCounter(step)
                    Text(
                        text = stringResource(Res.string.wizard_title_add),
                        style = MaterialTheme.typography.titleLarge,
                    )
                }
                CancelButton(onCancel)
            }
            WizardStepIndicator(step, Modifier.padding(start = 8.dp, end = 12.dp))
        }
        Box(Modifier.fillMaxWidth().height(1.dp).background(outline))
    }
}

@Composable
private fun StepCounter(step: WizardStep) {
    Text(
        text =
            if (step == WizardStep.PLACE) {
                stringResource(Res.string.wizard_step_counter_neutral, step.ordinal + 1)
            } else {
                stringResource(Res.string.wizard_step_counter, step.ordinal + 1)
            },
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

@Composable
internal fun CancelButton(onCancel: () -> Unit) {
    FruxButton(
        text = stringResource(Res.string.wizard_cancel),
        onClick = onCancel,
        style = FruxButtonStyle.Text,
    )
}
